// /mnt/mantenimientos/frontend/src/modules/monitorizacion/PcMonitorDetail.jsx
import React, { useMemo, useState } from "react";
import {
  normalizeZabbixEntry,
  fmtClock,
  fmtUptime,
  pct,
  gb,
  staleMinutes,
  isPfSense,
  filterPfSenseInterfaces,
  summarizeInterface,
  formatAgeFromClock,
  monitoringHealth,
  serviceState,
  severityInfo,
  simplifyOs,
} from "../../utils/zabbix";

function servicePill(label) {
  const state = serviceState(label);
  if (state.kind === "ok")
    return { cls: "badge badge-ok", icon: "✓", text: state.label };
  if (state.kind === "bad")
    return { cls: "badge badge-bad", icon: "!", text: state.label };
  if (state.kind === "na")
    return { cls: "badge badge-muted", icon: "—", text: state.label };
  return { cls: "badge badge-warn", icon: "?", text: state.label };
}

/** TeamViewer */
function openTeamViewer(id) {
  const tvId = String(id || "").trim();
  if (!tvId) return;

  const deepLink = `teamviewer://remotecontrol?connectid=${encodeURIComponent(tvId)}`;
  const webLink = `https://start.teamviewer.com/${encodeURIComponent(tvId)}`;

  window.location.href = deepLink;
  setTimeout(() => window.open(webLink, "_blank", "noopener,noreferrer"), 700);
}

function fmtMbps(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  if (v === 0) return "0";
  return v.toFixed(v < 10 ? 2 : v < 100 ? 1 : 0);
}

function pillForIface({ hasWarn } = {}) {
  if (hasWarn) return { cls: "badge badge-warn", text: "⚠️" };
  return { cls: "badge badge-ok", text: "✅" };
}

/** ✅ robust: admite "82", "82.5", "82,5", "82%" */
function toPctNumber(v) {
  if (v === null || v === undefined) return 0;
  const n = parseFloat(String(v).trim().replace("%", "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function ifaceRoleRank(role) {
  const r = String(role || "").toLowerCase();
  if (r === "wan") return 1;
  if (r === "lan") return 2;
  if (r === "opt") return 3;
  if (r === "vpn") return 4;
  return 9;
}

function prettyIfaceName(i) {
  // Preferimos ifdescr (WAN/LAN/OPT...), si no hay, ifname.
  const descr = String(i?.ifdescr || "").trim();
  const name = String(i?.ifname || "").trim();
  return descr || name || "—";
}

export default function PcMonitorDetail({
  pcActual,
  zabbixEntry,
  tab,
  setTab,
  position = 0,
  total = 0,
  onPrevious,
  onNext,
  onRefreshZabbix,
  loadingZabbix,
  onOpenEquipment,
  onOpenMaintenance,
}) {
  const [svcQ, setSvcQ] = useState("");

  const z = useMemo(() => normalizeZabbixEntry(zabbixEntry), [zabbixEntry]);
  const m = z.metricas;

  const staleMin = staleMinutes(m?.lastclock);
  const health = useMemo(() => monitoringHealth(zabbixEntry), [zabbixEntry]);

  const tvId = String(pcActual?.teamviewer_id || "").trim();
  const hasTv = !!tvId;

  // ✅ Detección robusta pfSense (evita falsos positivos)
  const pfSenseMode = useMemo(
    () => isPfSense(z.root || z.metricas),
    [z.root, z.metricas],
  );

  // ✅ Interfaces filtradas (solo activas, oculta enc0/pflog0/pfsync0/loopback)
  const pfIfaces = useMemo(() => {
    if (!pfSenseMode) return [];
    const base = Array.isArray(z.interfaces) ? z.interfaces : [];

    const filtered = filterPfSenseInterfaces(base, {
      onlyActive: true,
      hide: ["enc0", "pflog0", "pfsync0", "lo0", "lo"],
    });

    // ✅ Ordena por role (wan/lan/opt/vpn/other) y luego por nombre
    return [...filtered].sort((a, b) => {
      const ra = ifaceRoleRank(a?.role);
      const rb = ifaceRoleRank(b?.role);
      if (ra !== rb) return ra - rb;
      return prettyIfaceName(a).localeCompare(prettyIfaceName(b));
    });
  }, [pfSenseMode, z.interfaces]);

  const servicesFiltered = useMemo(() => {
    const query = String(svcQ || "")
      .trim()
      .toLowerCase();
    const arr = Array.isArray(z.services) ? z.services : [];
    const filtered = query
      ? arr.filter((s) =>
          String(s.name || "")
            .toLowerCase()
            .includes(query),
        )
      : arr;
    const rank = { bad: 0, warn: 1, unknown: 2, ok: 3, na: 4 };
    return [...filtered].sort((a, b) => {
      const delta =
        rank[serviceState(a.label).kind] - rank[serviceState(b.label).kind];
      return delta || String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [z.services, svcQ]);

  const serviceSummary = useMemo(() => {
    const summary = { ok: [], bad: [], warn: [], na: [], unknown: [] };
    z.services.forEach((service) => {
      const state = serviceState(service.label);
      (summary[state.kind] || summary.unknown).push(service);
    });
    return summary;
  }, [z.services]);

  // Tabs dinámicas
  const showDisksTab =
    Array.isArray(z.disks) && z.disks.length > 0 && !pfSenseMode;
  const showInterfacesTab = pfSenseMode;

  if (!pcActual) {
    return (
      <section className="mon-right">
        <div className="card" style={{ padding: 16 }}>
          Selecciona un equipo para ver el detalle de monitorización.
        </div>
      </section>
    );
  }

  return (
    <section className="mon-right">
      <div className="card mon-detail-head">
        <div className="mon-detail-title">
          <div className="mon-detail-h1">
            {pcActual.descripcion ||
              pcActual.hostname ||
              `Equipo ${pcActual.id}`}
            <span
              className={`badge badge-${health.kind === "unknown" ? "muted" : health.kind}`}
            >
              {health.label}
            </span>
            {pfSenseMode ? (
              <span className="chip chip-muted">pfSense</span>
            ) : null}
          </div>

          <div className="mon-detail-sub">
            {pcActual.hostname ? `🖥 ${pcActual.hostname}` : ""}
            {pcActual.ip ? ` · 🌐 ${pcActual.ip}` : ""}
            {pcActual.so ? ` · ${simplifyOs(pcActual.so)}` : ""}
            {z.host && z.host !== pcActual.hostname
              ? ` · Zabbix: ${z.host}`
              : ""}
          </div>

          <div className="mon-detail-kpis">
            <div className="mini-kpi">
              <div className="mini-kpi-label">CPU</div>
              <div className="mini-kpi-value">{pct(m?.cpu_pct)}</div>
            </div>
            <div className="mini-kpi">
              <div className="mini-kpi-label">RAM</div>
              <div className="mini-kpi-value">
                {pct(m?.ram_pct)}
                {Number.isFinite(Number(m?.ram_total_gb)) ? (
                  <span className="muted"> · {gb(m?.ram_total_gb)}</span>
                ) : null}
              </div>
            </div>
            <div className="mini-kpi">
              <div className="mini-kpi-label">Uptime</div>
              <div className="mini-kpi-value">{fmtUptime(m?.uptime_s)}</div>
            </div>
            <div className="mini-kpi">
              <div className="mini-kpi-label">Actualizado</div>
              <div className="mini-kpi-value">
                {fmtClock(m?.lastclock)}
                {staleMin != null ? (
                  <span className="muted"> · {staleMin}m</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="mon-detail-actions">
          <span className="mon-position" aria-live="polite">
            {position > 0 ? `${position} de ${total}` : `0 de ${total}`}
          </span>
          <button
            type="button"
            className="button secondary"
            onClick={onPrevious}
            disabled={!onPrevious}
            aria-label="Equipo anterior"
          >
            ← Anterior
          </button>
          <button
            type="button"
            className="button secondary"
            onClick={onNext}
            disabled={!onNext}
            aria-label="Equipo siguiente"
          >
            Siguiente →
          </button>
          <button
            type="button"
            className="button"
            onClick={() => onRefreshZabbix?.()}
            disabled={!onRefreshZabbix || !!loadingZabbix}
            title="Actualizar datos de Zabbix"
          >
            {loadingZabbix ? "⏳ Zabbix…" : "🔄 Refrescar Zabbix"}
          </button>

          {onOpenEquipment ? (
            <button
              type="button"
              className="button secondary"
              onClick={onOpenEquipment}
            >
              Abrir equipo
            </button>
          ) : null}

          {onOpenMaintenance ? (
            <button
              type="button"
              className="button secondary"
              onClick={onOpenMaintenance}
            >
              Registrar actuación
            </button>
          ) : null}

          {hasTv ? (
            <button
              type="button"
              className="button primary"
              onClick={() => openTeamViewer(tvId)}
              title="Abrir TeamViewer"
            >
              🔗 Conectar
            </button>
          ) : null}
        </div>
      </div>

      <div className="card mon-tabs">
        <button
          className={`mon-tab ${tab === "resumen" ? "active" : ""}`}
          onClick={() => setTab("resumen")}
        >
          Resumen
        </button>

        {showInterfacesTab ? (
          <button
            className={`mon-tab ${tab === "interfaces" ? "active" : ""}`}
            onClick={() => setTab("interfaces")}
          >
            Interfaces
          </button>
        ) : null}

        <button
          className={`mon-tab ${tab === "servicios" ? "active" : ""}`}
          onClick={() => setTab("servicios")}
        >
          Servicios
          {serviceSummary.bad.length ? ` (${serviceSummary.bad.length})` : ""}
        </button>

        {showDisksTab ? (
          <button
            className={`mon-tab ${tab === "discos" ? "active" : ""}`}
            onClick={() => setTab("discos")}
          >
            Discos
          </button>
        ) : null}

        <button
          className={`mon-tab ${tab === "problemas" ? "active" : ""}`}
          onClick={() => setTab("problemas")}
        >
          Problemas{z.problems.length ? ` (${z.problems.length})` : ""}
        </button>
      </div>

      <div className="card mon-detail-body">
        {!m ? (
          <div className="muted">No hay datos de Zabbix para este equipo.</div>
        ) : tab === "resumen" ? (
          <>
            <div className="section-label">Sistema</div>
            <div className="mon-grid2">
              <div className="mon-infoBox">
                <div className="muted">Sistema operativo</div>
                <div style={{ fontWeight: 900 }}>{simplifyOs(m.os) || "—"}</div>
              </div>
              <div className="mon-infoBox">
                <div className="muted">Agente de monitorización</div>
                <div style={{ fontWeight: 900 }}>
                  {m.agent_ping == null || m.agent_ping === ""
                    ? "⚪ Sin datos"
                    : Number(m.agent_ping) === 1
                      ? "🟢 Correcto"
                      : "🔴 Caído"}
                </div>
              </div>
            </div>

            {pfSenseMode ? (
              <div style={{ marginTop: 12 }}>
                <div className="section-label">Interfaces activas</div>

                {!pfIfaces.length ? (
                  <div className="muted">
                    No hay interfaces activas detectadas (o no hay datos).
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: 10,
                      marginTop: 8,
                    }}
                  >
                    {pfIfaces.map((i) => {
                      const s = summarizeInterface(i);
                      const pill = pillForIface(s);

                      const titleName = prettyIfaceName(i);
                      const sub = String(i?.ifname || "").trim();

                      return (
                        <div
                          key={`${i.ifname}-${titleName}`}
                          className="mon-infoBox"
                          style={{ padding: 12, borderRadius: 12 }}
                          title={`${titleName} (${sub}) · in=${i.in_bps} bps · out=${i.out_bps} bps · err=${s.errors} · drop=${s.drops}`}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <div style={{ fontWeight: 900 }}>
                              {titleName}
                              {titleName !== sub && sub ? (
                                <span
                                  className="muted"
                                  style={{ marginLeft: 8, fontWeight: 700 }}
                                >
                                  · {sub}
                                </span>
                              ) : null}
                            </div>
                            <span
                              className={pill.cls}
                              style={{ padding: "2px 8px" }}
                            >
                              {pill.text}
                            </span>
                          </div>

                          <div
                            className="muted"
                            style={{ marginTop: 6, fontSize: 12 }}
                          >
                            Entrada / Salida
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: 2,
                            }}
                          >
                            <div style={{ fontWeight: 800 }}>
                              ⬇ {fmtMbps(s.inMbps)} Mbps
                            </div>
                            <div style={{ fontWeight: 800 }}>
                              ⬆ {fmtMbps(s.outMbps)} Mbps
                            </div>
                          </div>

                          <div
                            className="muted"
                            style={{ marginTop: 6, fontSize: 12 }}
                          >
                            Errores / Drops
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: 2,
                            }}
                          >
                            <div style={{ fontWeight: 800 }}>{s.errors}</div>
                            <div style={{ fontWeight: 800 }}>{s.drops}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 12 }}>
                <div className="section-label">Servicios</div>
                {serviceSummary.bad.length || serviceSummary.warn.length ? (
                  <div className="chips-row">
                    {[...serviceSummary.bad, ...serviceSummary.warn].map(
                      (s) => {
                        const pill = servicePill(s.label);
                        return (
                          <span
                            key={s.name}
                            className={pill.cls}
                            title={`${s.name} | ${s.label}`}
                          >
                            {pill.icon} {s.name}: {pill.text}
                          </span>
                        );
                      },
                    )}
                  </div>
                ) : z.services.length ? (
                  <div className="mon-health-summary">
                    <span className="badge badge-ok">
                      ✓ {serviceSummary.ok.length} correctos
                    </span>
                    {serviceSummary.na.length ? (
                      <span className="badge badge-muted">
                        — {serviceSummary.na.length} no aplicables
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className="muted">Sin datos de servicios.</div>
                )}
              </div>
            )}
          </>
        ) : tab === "interfaces" ? (
          <>
            <div className="section-label">Interfaces (solo activas)</div>
            {!pfSenseMode ? (
              <div className="muted">
                Este equipo no es pfSense (o no hay datos de interfaces).
              </div>
            ) : !pfIfaces.length ? (
              <div className="muted">No hay interfaces activas detectadas.</div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: 12,
                }}
              >
                {pfIfaces.map((i) => {
                  const s = summarizeInterface(i);
                  const pill = pillForIface(s);

                  const titleName = prettyIfaceName(i);
                  const sub = String(i?.ifname || "").trim();

                  return (
                    <div
                      key={`${i.ifname}-${titleName}`}
                      className="mon-infoBox"
                      style={{ padding: 14, borderRadius: 14 }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div style={{ fontWeight: 900, fontSize: 14 }}>
                          {titleName}
                          {titleName !== sub && sub ? (
                            <span
                              className="muted"
                              style={{ marginLeft: 8, fontWeight: 700 }}
                            >
                              · {sub}
                            </span>
                          ) : null}
                        </div>
                        <span
                          className={pill.cls}
                          style={{ padding: "2px 8px" }}
                        >
                          {pill.text}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 10,
                          marginTop: 10,
                        }}
                      >
                        <div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            ⬇ Entrada
                          </div>
                          <div style={{ fontWeight: 900 }}>
                            {fmtMbps(s.inMbps)} Mbps
                          </div>
                        </div>
                        <div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            ⬆ Salida
                          </div>
                          <div style={{ fontWeight: 900 }}>
                            {fmtMbps(s.outMbps)} Mbps
                          </div>
                        </div>
                        <div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            Errores
                          </div>
                          <div style={{ fontWeight: 900 }}>{s.errors}</div>
                        </div>
                        <div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            Drops
                          </div>
                          <div style={{ fontWeight: 900 }}>{s.drops}</div>
                        </div>
                      </div>

                      {s.hasWarn ? (
                        <div
                          className="muted"
                          style={{ marginTop: 10, fontSize: 12 }}
                        >
                          ⚠️ Revisa errores/drops en esta interfaz.
                        </div>
                      ) : (
                        <div
                          className="muted"
                          style={{ marginTop: 10, fontSize: 12 }}
                        >
                          ✅ Sin errores/drops.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : tab === "servicios" ? (
          <>
            <div className="mon-svc-head">
              <div className="section-label" style={{ margin: 0 }}>
                Servicios
              </div>
              <input
                className="input mon-svc-search"
                placeholder="Buscar servicio…"
                value={svcQ}
                onChange={(e) => setSvcQ(e.target.value)}
              />
            </div>

            <div
              className="mon-service-summary"
              aria-label="Resumen de estados de servicios"
            >
              {serviceSummary.bad.length ? (
                <span className="badge badge-bad">
                  {serviceSummary.bad.length} caídos
                </span>
              ) : null}
              {serviceSummary.warn.length ? (
                <span className="badge badge-warn">
                  {serviceSummary.warn.length} a revisar
                </span>
              ) : null}
              {serviceSummary.ok.length ? (
                <span className="badge badge-ok">
                  {serviceSummary.ok.length} correctos
                </span>
              ) : null}
              {serviceSummary.na.length ? (
                <span className="badge badge-muted">
                  {serviceSummary.na.length} no aplicables
                </span>
              ) : null}
            </div>

            {!servicesFiltered.length ? (
              <div className="muted">
                No hay servicios en Zabbix (o no existen items para esta
                plantilla/host).
              </div>
            ) : (
              <div className="mon-svc-table">
                {servicesFiltered.map((s) => {
                  const pill = servicePill(s.label);
                  return (
                    <div key={s.name} className="mon-svc-row">
                      <div className="mon-svc-name">{s.name}</div>
                      <div className="mon-svc-state">
                        <span className={pill.cls}>
                          {pill.icon} {pill.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : tab === "discos" ? (
          <>
            <div className="section-label">Discos</div>
            {!Array.isArray(z.disks) || z.disks.length === 0 ? (
              <div className="muted">—</div>
            ) : (
              <div className="disk-list">
                {z.disks.map((d) => {
                  const used = toPctNumber(d.used_pct);
                  const warn = used >= 90;

                  return (
                    <div key={d.drive} className="disk-item">
                      <div className="disk-top">
                        <div className="disk-drive">
                          {d.drive} {warn ? "⚠️" : ""}
                        </div>
                        <div className="disk-sub">
                          {gb(d.free_gb)} libres / {gb(d.total_gb)}
                        </div>
                      </div>

                      <div className="disk-bar">
                        <span
                          className={`disk-bar-fill ${warn ? "warn" : ""}`}
                          style={{
                            width: `${Math.max(0, Math.min(100, used))}%`,
                          }}
                        />
                      </div>

                      <div className="disk-pct">{pct(used)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : tab === "problemas" ? (
          <>
            <div className="section-label">Problemas</div>
            {!Array.isArray(z.problems) || z.problems.length === 0 ? (
              <div className="muted">Sin problemas activos.</div>
            ) : (
              <div className="mon-problems">
                {[...z.problems]
                  .sort(
                    (a, b) =>
                      Number(b.severity || 0) - Number(a.severity || 0) ||
                      Number(a.clock || 0) - Number(b.clock || 0),
                  )
                  .slice(0, 50)
                  .map((p, idx) => {
                    const severity = severityInfo(p.severity);
                    return (
                      <article
                        className="mon-problem"
                        key={p.eventid || p.triggerid || `${p.name}-${idx}`}
                      >
                        <div className="mon-problem-head">
                          <span className={`badge badge-${severity.kind}`}>
                            {severity.label}
                          </span>
                          <span className="mon-problem-age">
                            {formatAgeFromClock(p.clock)}
                          </span>
                        </div>
                        <div className="mon-problem-title">
                          {p.name || "Problema sin descripción"}
                        </div>
                        <div className="mon-problem-meta">
                          {p.host ? <span>Equipo: {p.host}</span> : null}
                          <span>
                            {Number(p.acknowledged) === 1
                              ? "Reconocido"
                              : "Pendiente de reconocer"}
                          </span>
                        </div>
                      </article>
                    );
                  })}
              </div>
            )}
          </>
        ) : (
          <div className="muted">Selecciona una sección de monitorización.</div>
        )}
      </div>
    </section>
  );
}
